export type ForecastDirection = 'stable' | 'rising' | 'variable';

export type ForecastV2Result = {
  direction: ForecastDirection;
  probabilityRisePct: number;
  confidencePct: number;
  horizonHours: number;
  trendDelta: number;
  volatility: number;
};

export type PatternInsight = {
  key: 'volatility_spike' | 'withdrawal_loop' | 'recovery_arc' | 'overcorrection_cycle';
  label: string;
  description: string;
};

export type ChurnRiskResult = {
  riskPct: number;
  level: 'low' | 'medium' | 'high';
};

export type ConversationScript = {
  title: string;
  text: string;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function movingAverage(values: number[]): number {
  if (!values.length) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

export function computeForecastV2(scores: number[]): ForecastV2Result {
  const list = scores.filter((n) => Number.isFinite(n)).slice(-14);
  if (list.length <= 1) {
    const base = list[0] ?? 0;
    return {
      direction: base >= 61 ? 'rising' : base <= 35 ? 'stable' : 'variable',
      probabilityRisePct: clamp(Math.round(base), 5, 95),
      confidencePct: 25,
      horizonHours: 72,
      trendDelta: 0,
      volatility: 0,
    };
  }

  const deltas: Array<number> = [];
  for (let i = 1; i < list.length; i += 1) deltas.push(list[i] - list[i - 1]);
  const trendDelta = movingAverage(deltas);
  const variance = movingAverage(deltas.map((d) => Math.abs(d)));
  const recentAvg = movingAverage(list.slice(-5));
  const priorAvg = movingAverage(list.slice(0, Math.max(1, list.length - 5)));
  const longDelta = recentAvg - priorAvg;

  const riseSignal = (trendDelta * 1.7) + (longDelta * 0.9) + ((recentAvg - 50) * 0.55);
  const probabilityRisePct = clamp(Math.round(50 + riseSignal), 5, 95);
  const confidencePct = clamp(Math.round(35 + (list.length * 3.5) - (variance * 1.8)), 20, 92);

  const direction: ForecastDirection =
    probabilityRisePct >= 60 ? 'rising' : probabilityRisePct <= 42 ? 'stable' : 'variable';

  return {
    direction,
    probabilityRisePct,
    confidencePct,
    horizonHours: 72,
    trendDelta: Number(trendDelta.toFixed(2)),
    volatility: Number(variance.toFixed(2)),
  };
}

export function detectPatternLibrary(scores: number[], withdrawalSignals: number[] = []): Array<PatternInsight> {
  const list = scores.filter((n) => Number.isFinite(n)).slice(-14);
  if (!list.length) return [];
  const result: Array<PatternInsight> = [];

  const min = Math.min(...list);
  const max = Math.max(...list);
  const swing = max - min;
  if (swing >= 22) {
    result.push({
      key: 'volatility_spike',
      label: 'Volatility Spike',
      description: 'Large score swings suggest a fragile loop where timing and pacing matter more than intensity.',
    });
  }

  const tail = list.slice(-4);
  if (tail.length >= 4 && tail[0] > tail[1] && tail[1] > tail[2] && tail[2] > tail[3]) {
    result.push({
      key: 'recovery_arc',
      label: 'Recovery Arc',
      description: 'Recent trend is moving toward repair. Reinforce behaviors that supported this downward tension arc.',
    });
  }

  const withdrawalAvg = withdrawalSignals.length ? movingAverage(withdrawalSignals.slice(-7)) : 0;
  if (withdrawalAvg >= 62) {
    result.push({
      key: 'withdrawal_loop',
      label: 'Withdrawal Loop',
      description: 'Persistent withdrawal markers indicate avoidant cycles. Use short, low-pressure repair prompts.',
    });
  }

  if (list.length >= 6) {
    const last = list.slice(-6);
    const spikes = last.filter((s, idx) => idx > 0 && Math.abs(s - last[idx - 1]) >= 15).length;
    if (spikes >= 2) {
      result.push({
        key: 'overcorrection_cycle',
        label: 'Overcorrection Cycle',
        description: 'Rapid back-and-forth moves suggest over-adjusting after conflict. Slower interventions are recommended.',
      });
    }
  }

  return result.slice(0, 3);
}

export function computeChurnRisk(scores: number[], forecast: ForecastV2Result): ChurnRiskResult {
  const list = scores.filter((n) => Number.isFinite(n)).slice(-14);
  if (!list.length) return { riskPct: 0, level: 'low' };
  const avg = movingAverage(list);
  const max = Math.max(...list);
  const base = (avg * 0.45) + (max * 0.2) + (forecast.probabilityRisePct * 0.35);
  const riskPct = clamp(Math.round(base), 1, 99);
  const level = riskPct >= 68 ? 'high' : riskPct >= 42 ? 'medium' : 'low';
  return { riskPct, level };
}

export function generateConversationScripts(input: {
  rtiScore: number;
  attachmentStyle?: 'secure' | 'anxious' | 'avoidant' | 'fearful' | string;
  topPattern?: PatternInsight;
}): Array<ConversationScript> {
  const style = input.attachmentStyle || 'secure';
  const pattern = input.topPattern?.label || 'Current Tension Pattern';
  const heat = input.rtiScore >= 61 ? 'high' : input.rtiScore <= 35 ? 'low' : 'moderate';

  const scripts: Array<ConversationScript> = [
    {
      title: 'Repair opener',
      text:
        heat === 'high'
          ? `I want to slow this down. I care about us more than winning this moment. Can we revisit after a short reset?`
          : `I care about how we handled this. Can we take 10 minutes to map what worked and what we want to repeat?`,
    },
    {
      title: `${pattern} script`,
      text: `I noticed a ${pattern.toLowerCase()} pattern this week. Instead of forcing a long talk now, can we try one small next step and check in tomorrow?`,
    },
    {
      title: 'Attachment-aware line',
      text:
        style === 'anxious'
          ? `I need reassurance, and I also want to stay calm. Could you share one thing that still feels solid between us?`
          : style === 'avoidant'
            ? `I need a little space to think, but I do want to return to this. Can we pick a time tonight to reconnect?`
            : style === 'fearful'
              ? `Part of me wants to pull away and part wants to fix this. Can we keep this gentle and specific for 10 minutes?`
              : `I want to understand your side clearly before I react. Can we each share one need and one boundary?`,
    },
  ];

  return scripts;
}
